import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Avatar } from 'primereact/avatar';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useNavigate } from 'react-router-dom';
import StudentEnrollmentModal from './parts/StudentEnrollmentModal';
import BulkManageEnrollmentsModal from './parts/BulkManageEnrollmentsModal';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
import BatchSelector, { BatchOption } from '../MarksRecorder/parts/BatchSelector';

/* ─── Design tokens ────────────────────────────────────────────────── */
const C = {
  navy: '#0e225d',
  navyMid: '#1a3370',
  navyDark: '#0a1a3d',
  navyHover: 'rgba(14,34,93,0.06)',
  gold: '#fbb21f',
  goldLight: '#ffc93c',
  goldDim: 'rgba(251,178,31,0.14)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white: '#ffffff',
  border: '#e5e7eb',
  muted: '#9ca3af',
  sub: '#6b7280',
  body: '#1f2937',
  green: '#16a34a',
  greenDim: 'rgba(22,163,74,0.12)',
  greenBorder: 'rgba(22,163,74,0.3)',
  red: '#dc2626',
  redDim: 'rgba(220,38,38,0.08)',
  redBorder: 'rgba(220,38,38,0.25)',
};

/* ─── Interfaces ───────────────────────────────────────────────────── */
interface ProfilePicture {
  file_name: string;
  key: string;
  cdn_url: string;
  dev_url: string;
  size: number;
  mime_type: string;
  etag: string;
  uploaded_at: { $date: { $numberLong: string } };
}

interface EnrollmentDetails {
  is_enrolled: boolean;
  course_id: string | null;
  course_name: string | null;
  batch_id: string | null;
  batch_name: string | null;
  date_of_admission: string | null;
}

interface Student {
  student_id: string;
  name: string;
  dateOfBirth: string | null;
  gender: string | null;
  aadharNumber: string | null;
  category: string | null;
  mobileNumber: string | null;
  district: string | null;
  state: string | null;
  pinCode: string | null;
  bloodGroup: string | null;
  whatsappNumber: string | null;
  email: string | null;
  villageTown: string | null;
  status: string;
  created_at: string | null;
  profilePicture: ProfilePicture | null;
  enrollment: EnrollmentDetails;
  is_registered?: boolean;
}

interface ApiResponse {
  success: boolean;
  data: Student[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

/* Shape returned by GET /v1/students/get-students-by-batch/{batch_id}
   (studentControllers.php → getStudentsByBatchController). Its rows don't
   carry a full `enrollment` object like the unfiltered list does — instead
   they carry a flat `date_of_admission` string plus a shared `batch_info`
   block for the whole page. We normalize both shapes into `Student` below. */
interface BatchStudentRow {
  student_id: string;
  student_custom_id?: string | null;
  name: string;
  dateOfBirth: string | null;
  gender: string | null;
  aadharNumber: string | null;
  category: string | null;
  mobileNumber: string | null;
  district: string | null;
  state: string | null;
  pinCode: string | null;
  bloodGroup: string | null;
  whatsappNumber: string | null;
  email: string | null;
  villageTown: string | null;
  status: string;
  created_at: string | null;
  profilePicture: ProfilePicture | null;
  date_of_admission: string | null;
  is_registered?: boolean;
}

interface BatchInfo {
  batch_id: string;
  batch_name: string;
  batch_time: string;
  course_id: string | null;
  course_name?: string;
  course_title?: string;
  start_month?: string;
  start_year?: number | null;
  end_month?: string;
  end_year?: number | null;
  is_active: boolean;
}

interface BatchStudentsApiResponse {
  success: boolean;
  data: BatchStudentRow[];
  batch_info: BatchInfo;
  filters: { status: string };
  pagination: { page: number; limit: number; total: number; pages: number };
}

/* Shape returned by GET /v1/batches — the same endpoint DocumentGenerator
   already uses successfully. (The earlier `/v1/batches/get-batches` guess
   was wrong and was very likely being swallowed by a parameterized
   `/v1/batches/{batch_id}` route, with the literal string "get-batches"
   getting treated as the batch id — which is exactly the shape of the
   "Batch ID required" error this produced.) */
interface BatchListItem extends BatchOption {
  course_id?: string;
  course_name?: string;
  course_title?: string;
  is_active: boolean;
}

type BatchStatusFilter = 'active' | 'inactive' | 'all';

/* ─── Stat card sub-component ──────────────────────────────────────── */
const StatCard = ({
  icon,
  label,
  value,
  sub,
  subPositive,
}: {
  icon: string;
  label: string;
  value: string | number;
  sub?: string;
  subPositive?: boolean;
}) => (
  <div
    style={{
      flex: '1 1 180px',
      background: C.white,
      border: `1px solid ${C.border}`,
      borderRadius: '14px',
      padding: '10px 15px',
      boxShadow: '0 1px 4px rgba(14,34,93,0.06)',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <span style={{ fontSize: '0.78rem', fontWeight: '600', color: C.muted, letterSpacing: '0.3px' }}>
        {label}
      </span>
      <div
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          background: C.goldDim,
          border: `1px solid ${C.goldBorder}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <i className={icon} style={{ fontSize: '0.85rem', color: C.gold }} />
      </div>
    </div>
    <div style={{ fontSize: '1.2rem', fontWeight: '800', color: C.navy, lineHeight: 1.1 }}>
      {value}
    </div>
    {sub && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
        <span
          style={{
            fontSize: '0.72rem',
            fontWeight: '700',
            padding: '2px 8px',
            borderRadius: '20px',
            background: subPositive ? C.greenDim : C.redDim,
            color: subPositive ? C.green : C.red,
            border: `1px solid ${subPositive ? C.greenBorder : C.redBorder}`,
          }}
        >
          {sub}
        </span>
        <span style={{ fontSize: '0.72rem', color: C.muted }}>vs last month</span>
      </div>
    )}
  </div>
);

/* ─── Main Component ────────────────────────────────────────────────── */
const Students: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useOperatorProfileStore();
  const INSTITUTE_ID = profile?.institute_id;
  const BRANCH_ID = profile?.branch_id;

  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [tempSearch, setTempSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });
  const [selectedStudents, setSelectedStudents] = useState<Student[] | null>(null);
  const [showStats, setShowStats] = useState(false); // Default: OFF

  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [enrollingStudent, setEnrollingStudent] = useState<{
    id: string;
    name: string;
    existingEnrollments: Array<{ course_id: string; batch_id: string; date_of_admission: string }>;
  } | null>(null);

  const [showBulkManageModal, setShowBulkManageModal] = useState(false);
  const [bulkManageDefaultTab, setBulkManageDefaultTab] = useState<'enroll' | 'deenroll'>('enroll');

  /* ── Batch filter state ── */
  const [showBatchFilter, setShowBatchFilter] = useState(false);
  const [batches, setBatches] = useState<BatchListItem[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [batchesError, setBatchesError] = useState<string | null>(null);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [batchStatusFilter, setBatchStatusFilter] = useState<BatchStatusFilter>('active');
  const [activeBatchInfo, setActiveBatchInfo] = useState<BatchInfo | null>(null);

  const isBatchFiltered = !!selectedBatchId;

  /* Fetch the list of batches to populate BatchSelector. Only loaded once
     the filter panel is opened, so it doesn't cost a request on every page
     load for people who never filter by batch. */
  const fetchBatches = async () => {
    try {
      setBatchesLoading(true);
      setBatchesError(null);
      const res = await apiClient.get('/v1/batches');
      if (res.data.success === false) {
        setBatchesError(res.data.error || 'Failed to load batches');
        return;
      }
      const rows = res.data?.data ?? [];
      const mapped: BatchListItem[] = rows.map((b: any) => ({
        batch_id: b.batch_id,
        batch_name: b.batch_name,
        batch_time: b.batch_time,
        course_id: b.course_id,
        course_name: b.course_name,
        course_title: b.course_title,
        is_active: !!b.is_active,
      }));
      setBatches(mapped);
    } catch (err: any) {
      setBatchesError(err.response?.data?.error || 'Failed to load batches');
    } finally {
      setBatchesLoading(false);
    }
  };

  // Batch status here filters which batches are offered in the selector
  // (client-side, from each batch's own `is_active` flag) — it is NOT sent
  // to the by-batch student endpoint. That endpoint's own `status` param
  // re-validates the *specific* batch id against is_active, so passing the
  // filter value there would wrongly 404 ("not found") the moment someone
  // picks an inactive batch while a different filter chip was active.
  const filteredBatches = batches.filter((b) => {
    if (batchStatusFilter === 'all') return true;
    if (batchStatusFilter === 'active') return b.is_active;
    return !b.is_active;
  });

  useEffect(() => {
    if (showBatchFilter && batches.length === 0 && !batchesLoading) {
      fetchBatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBatchFilter]);

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search, selectedBatchId]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      setError(null);

      if (selectedBatchId) {
        // Batch-filtered list
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          status: 'all', // batch already chosen explicitly — don't re-filter by its is_active flag here
          ...(search && { search }),
        });

        const response = await apiClient.get<BatchStudentsApiResponse>(
          `/v1/students/get-students-by-batch/${selectedBatchId}?${params}`
        );

        if (response.data.success) {
          const batchInfo = response.data.batch_info;
          setActiveBatchInfo(batchInfo);

          const normalized: Student[] = response.data.data.map((row) => ({
            student_id: row.student_id,
            name: row.name,
            dateOfBirth: row.dateOfBirth,
            gender: row.gender,
            aadharNumber: row.aadharNumber,
            category: row.category,
            mobileNumber: row.mobileNumber,
            district: row.district,
            state: row.state,
            pinCode: row.pinCode,
            bloodGroup: row.bloodGroup,
            whatsappNumber: row.whatsappNumber,
            email: row.email,
            villageTown: row.villageTown,
            status: row.status,
            created_at: row.created_at,
            profilePicture: row.profilePicture,
            is_registered: row.is_registered,
            enrollment: {
              is_enrolled: true,
              course_id: batchInfo.course_id,
              course_name: batchInfo.course_name || null,
              batch_id: batchInfo.batch_id,
              batch_name: batchInfo.batch_name,
              date_of_admission: row.date_of_admission,
            },
          }));

          setStudents(normalized);
          setPagination(response.data.pagination);
        } else {
          setError('Failed to load students');
        }
      } else {
        // Unfiltered list (all students in institute/branch)
        setActiveBatchInfo(null);

        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          ...(search && { search }),
        });

        const response = await apiClient.get<ApiResponse>(`/v1/students/get-students?${params}`);

        if (response.data.success) {
          setStudents(response.data.data);
          setPagination(response.data.pagination);
        } else {
          setError('Failed to load students');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearch(tempSearch);
    setPage(1);
  };

  const handleSelectBatch = (batchId: string) => {
    // Toggle off if the same chip is clicked again
    setSelectedBatchId((current) => (current === batchId ? null : batchId));
    setPage(1);
    setSelectedStudents(null);
  };

  const handleClearBatchFilter = () => {
    setSelectedBatchId(null);
    setPage(1);
    setSelectedStudents(null);
  };

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return d;
    }
  };

  /* ── Column Templates ── */
  const studentTemplate = (row: Student) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <Avatar
          image={row.profilePicture?.cdn_url || undefined}
          icon={!row.profilePicture ? 'pi pi-user' : undefined}
          size="normal"
          shape="circle"
          style={{
            width: '36px',
            height: '36px',
            backgroundColor: row.profilePicture ? 'transparent' : '#e8eef7',
            color: C.navy,
            border: `2px solid ${C.border}`,
          }}
        />
        {row.is_registered && (
          <div
            style={{
              position: 'absolute',
              bottom: '-2px',
              right: '-2px',
              width: '12px',
              height: '12px',
              background: C.green,
              borderRadius: '50%',
              border: '2px solid white',
            }}
          />
        )}
      </div>
      <div>
        <div style={{ fontWeight: '600', color: C.navy, fontSize: '0.84rem' }}>{row.name}</div>
        {row.mobileNumber && (
          <div style={{ fontSize: '0.71rem', color: C.muted }}>{row.mobileNumber}</div>
        )}
      </div>
    </div>
  );

  const statusTemplate = (row: Student) => (
    <span
      style={{
        padding: '3px 10px',
        borderRadius: '20px',
        fontSize: '0.71rem',
        fontWeight: '700',
        textTransform: 'capitalize',
        display: 'inline-block',
        ...(row.status === 'active'
          ? { background: C.greenDim, color: C.green, border: `1px solid ${C.greenBorder}` }
          : { background: C.redDim, color: C.red, border: `1px solid ${C.redBorder}` }),
      }}
    >
      {row.status}
    </span>
  );

  const registrationTemplate = (row: Student) => (
    <span
      style={{
        padding: '3px 10px',
        borderRadius: '20px',
        fontSize: '0.71rem',
        fontWeight: '700',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        ...(row.is_registered
          ? { background: 'rgba(14,34,93,0.08)', color: C.navy, border: '1px solid rgba(14,34,93,0.2)' }
          : { background: C.goldDim, color: '#b8860b', border: `1px solid ${C.goldBorder}` }),
      }}
    >
      <i className={row.is_registered ? 'pi pi-check-circle' : 'pi pi-exclamation-triangle'} style={{ fontSize: '0.65rem' }} />
      {row.is_registered ? 'Registered' : 'Not Registered'}
    </span>
  );

  const enrollmentTemplate = (row: Student) => (
    <span
      style={{
        padding: '3px 10px',
        borderRadius: '20px',
        fontSize: '0.71rem',
        fontWeight: '700',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        ...(row.enrollment.is_enrolled
          ? { background: C.greenDim, color: C.green, border: `1px solid ${C.greenBorder}` }
          : { background: C.goldDim, color: '#b8860b', border: `1px solid ${C.goldBorder}` }),
      }}
    >
      <i className={row.enrollment.is_enrolled ? 'pi pi-check-circle' : 'pi pi-exclamation-triangle'} style={{ fontSize: '0.65rem' }} />
      {row.enrollment.is_enrolled ? 'Enrolled' : 'Not Enrolled'}
    </span>
  );

  const actionsTemplate = (row: Student) => (
    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
      {[
        { icon: 'pi pi-eye', title: 'View', hover: { bg: C.navyHover, border: 'rgba(14,34,93,0.2)', color: C.navy }, action: () => navigate(`/students/${row.student_id}`) },
        { icon: 'pi pi-pencil', title: 'Edit', hover: { bg: C.goldDim, border: C.goldBorder, color: '#b8860b' }, action: () => navigate(`/students/edit/${row.student_id}`) },
        {
          icon: 'pi pi-graduation-cap',
          title: 'Enroll',
          hover: { bg: C.greenDim, border: C.greenBorder, color: C.green },
          action: async () => {
            try {
              const res = await apiClient.get(`/v1/students/get-student/${row.student_id}`);
              if (res.data.success) {
                const s = res.data.data;
                setEnrollingStudent({
                  id: s.student_id,
                  name: s.name,
                  existingEnrollments: s.enrollments?.map((e: any) => ({
                    course_id: e.course_id || '',
                    batch_id: e.batch_id || '',
                    date_of_admission: e.date_of_admission || '',
                  })) || [],
                });
              }
            } catch {
              setEnrollingStudent({ id: row.student_id, name: row.name, existingEnrollments: [] });
            }
            setShowEnrollmentModal(true);
          },
        },
      ].map(({ icon, title, hover, action }) => (
        <button
          key={icon}
          onClick={action}
          title={title}
          style={{
            width: '30px',
            height: '30px',
            background: 'transparent',
            border: `1px solid ${C.border}`,
            borderRadius: '7px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: C.muted,
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = hover.bg;
            e.currentTarget.style.borderColor = hover.border;
            e.currentTarget.style.color = hover.color;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = C.border;
            e.currentTarget.style.color = C.muted;
          }}
        >
          <i className={icon} style={{ fontSize: '0.78rem' }} />
        </button>
      ))}
    </div>
  );

  /* ── Pagination ── */
  const renderPages = () => {
    const total = pagination.pages;
    const pages: (number | string)[] = [];

    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else if (page <= 3) {
      pages.push(1, 2, 3, '...', total);
    } else if (page >= total - 2) {
      pages.push(1, '...', total - 2, total - 1, total);
    } else {
      pages.push(1, '...', page, '...', total);
    }

    return pages.map((p, i) =>
      p === '...' ? (
        <span key={`ellipsis-${i}`} style={{ padding: '0 4px', color: C.muted, fontSize: '0.82rem' }}>
          …
        </span>
      ) : (
        <button
          key={p}
          onClick={() => setPage(p as number)}
          style={{
            minWidth: '32px',
            height: '32px',
            padding: '0 8px',
            borderRadius: '7px',
            fontSize: '0.82rem',
            fontWeight: page === p ? '700' : '500',
            cursor: 'pointer',
            background: page === p ? C.navy : 'transparent',
            color: page === p ? C.gold : C.navy,
            border: page === p ? 'none' : `1px solid ${C.border}`,
            boxShadow: page === p ? '0 2px 6px rgba(14,34,93,0.25)' : 'none',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            if (page !== p) e.currentTarget.style.background = C.navyHover;
          }}
          onMouseLeave={(e) => {
            if (page !== p) e.currentTarget.style.background = 'transparent';
          }}
        >
          {p}
        </button>
      )
    );
  };

  if (loading && students.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          background: C.white,
          borderRadius: '16px',
          border: `1px solid ${C.border}`,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
          <p style={{ marginTop: '14px', color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>
            Loading students…
          </p>
        </div>
      </div>
    );
  }

  const selected = selectedStudents ?? [];

  return (
    <>
      <style>{`
        .sdt .p-datatable-thead > tr > th {
          background: #fafafa !important;
          color: ${C.muted} !important;
          font-size: 0.7rem !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.6px !important;
          border-bottom: 2px solid ${C.border} !important;
          padding: 12px 14px !important;
        }
        .sdt .p-datatable-tbody > tr { transition: background 0.12s; }
        .sdt .p-datatable-tbody > tr:hover > td { background: rgba(14,34,93,0.025) !important; }
        .sdt .p-datatable-tbody > tr.p-highlight > td { background: rgba(251,178,31,0.06) !important; }
        .sdt .p-datatable-tbody > tr > td {
          border-bottom: 1px solid #f3f4f6 !important;
          padding: 10px 12px !important;
          font-size: 0.83rem;
        }
        .sdt .p-checkbox .p-checkbox-box.p-highlight { background: ${C.navy} !important; border-color: ${C.navy} !important; }
        .sdt .p-checkbox .p-checkbox-box:hover { border-color: ${C.gold} !important; }
        .sdt .p-datatable-loading-overlay { background: rgba(255,255,255,0.7) !important; }
        .sdt .p-datatable-tbody > tr.p-highlight { border-left: 3px solid ${C.gold}; }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 14px',
                background: C.white,
                border: `1px solid ${C.border}`,
                borderRadius: '9px',
                fontSize: '0.82rem',
                fontWeight: '600',
                color: C.navy,
                cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(14,34,93,0.06)',
              }}
            >
              <i className="pi pi-table" style={{ fontSize: '0.8rem' }} />
              Table View
              <i className="pi pi-chevron-down" style={{ fontSize: '0.65rem', marginLeft: '2px', color: C.muted }} />
            </div>

            {/* Filter by Batch toggle */}
            <button
              onClick={() => setShowBatchFilter((s) => !s)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                padding: '10px 14px',
                background: showBatchFilter || isBatchFiltered ? C.navy : C.white,
                border: `1px solid ${showBatchFilter || isBatchFiltered ? C.navy : C.border}`,
                borderRadius: '9px',
                fontSize: '0.82rem',
                fontWeight: '700',
                color: showBatchFilter || isBatchFiltered ? C.gold : C.sub,
                cursor: 'pointer',
                boxShadow: showBatchFilter || isBatchFiltered ? '0 2px 8px rgba(14,34,93,0.25)' : '0 1px 3px rgba(14,34,93,0.06)',
                transition: 'all 0.15s ease',
              }}
            >
              <i className="pi pi-filter" style={{ fontSize: '0.78rem' }} />
              Filter by Batch
              {isBatchFiltered && (
                <span
                  style={{
                    width: '7px',
                    height: '7px',
                    borderRadius: '50%',
                    background: C.gold,
                    marginLeft: '2px',
                  }}
                />
              )}
            </button>

            {/* Show Statistics Toggle */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '7px 14px',
                background: C.white,
                border: `1px solid ${C.border}`,
                borderRadius: '9px',
                fontSize: '0.82rem',
                fontWeight: '600',
                color: C.sub,
              }}
            >
              Show Statistics
              <div
                onClick={() => setShowStats((s) => !s)}
                style={{
                  width: '38px',
                  height: '22px',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  background: showStats ? C.navy : C.muted,
                  position: 'relative',
                  transition: 'background 0.2s',
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '3px',
                    left: showStats ? '18px' : '3px',
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    background: showStats ? C.gold : C.white,
                    transition: 'left 0.2s, background 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }}
                />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {/* Search */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                background: C.white,
                border: `1px solid ${C.border}`,
                borderRadius: '9px',
                overflow: 'hidden',
              }}
            >
              <input
                value={tempSearch}
                onChange={(e) => setTempSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search students…"
                style={{
                  padding: '10px 12px',
                  border: 'none',
                  outline: 'none',
                  fontSize: '0.82rem',
                  color: C.body,
                  minWidth: '200px',
                  background: 'transparent',
                }}
              />
              <button
                onClick={handleSearch}
                style={{
                  padding: '10px 14px',
                  background: C.navy,
                  border: 'none',
                  color: C.gold,
                  cursor: 'pointer',
                  fontSize: '0.82rem',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <i className="pi pi-search" style={{ fontSize: '0.78rem' }} />
              </button>
            </div>

            <button
              onClick={() => navigate('/students/add-student')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                padding: '10px 18px',
                background: C.navy,
                border: 'none',
                borderRadius: '9px',
                color: C.white,
                fontSize: '0.82rem',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(14,34,93,0.25)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = C.navyMid;
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = C.navy;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <i className="pi pi-plus" style={{ fontSize: '0.82rem' }} />
              Add New Student
            </button>
          </div>
        </div>

        {error && <Message severity="error" text={error} style={{ width: '100%', fontSize: '0.88rem' }} />}

        {/* Batch Filter Panel */}
        {showBatchFilter && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.sub }}>Batch status</span>
                <div
                  style={{
                    display: 'flex',
                    background: C.white,
                    border: `1px solid ${C.border}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  {(['active', 'inactive', 'all'] as BatchStatusFilter[]).map((s) => (
                    <button
                      key={s}
                      onClick={() => {
                        setBatchStatusFilter(s);
                        setPage(1);
                      }}
                      style={{
                        padding: '6px 14px',
                        border: 'none',
                        borderRight: s !== 'all' ? `1px solid ${C.border}` : 'none',
                        background: batchStatusFilter === s ? C.navy : 'transparent',
                        color: batchStatusFilter === s ? C.gold : C.sub,
                        fontSize: '0.76rem',
                        fontWeight: '700',
                        textTransform: 'capitalize',
                        cursor: 'pointer',
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {isBatchFiltered && (
                <button
                  onClick={handleClearBatchFilter}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    background: C.redDim,
                    border: `1px solid ${C.redBorder}`,
                    borderRadius: '8px',
                    color: C.red,
                    fontSize: '0.76rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                  }}
                >
                  <i className="pi pi-times" style={{ fontSize: '0.68rem' }} />
                  Clear batch filter
                </button>
              )}
            </div>

            {batchesError && <Message severity="error" text={batchesError} style={{ width: '100%', fontSize: '0.85rem' }} />}

            {batchesLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px' }}>
                <ProgressSpinner style={{ width: '22px', height: '22px' }} strokeWidth="4" />
                <span style={{ fontSize: '0.82rem', color: C.muted }}>Loading batches…</span>
              </div>
            ) : (
              <BatchSelector batches={filteredBatches} selectedBatchId={selectedBatchId} onSelect={handleSelectBatch} />
            )}
          </div>
        )}

        {/* Active batch context strip */}
        {isBatchFiltered && activeBatchInfo && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '10px',
              padding: '10px 16px',
              background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(14,34,93,0.2)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <i className="pi pi-th-large" style={{ color: C.gold, fontSize: '0.85rem' }} />
              <span style={{ color: 'white', fontSize: '0.84rem', fontWeight: '700' }}>
                {activeBatchInfo.batch_name}
              </span>
              {activeBatchInfo.batch_time && (
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>
                  {activeBatchInfo.batch_time}
                </span>
              )}
              {activeBatchInfo.course_name && (
                <span
                  style={{
                    padding: '2px 9px',
                    borderRadius: '20px',
                    background: 'rgba(251,178,31,0.18)',
                    color: C.gold,
                    fontSize: '0.72rem',
                    fontWeight: '700',
                  }}
                >
                  {activeBatchInfo.course_name}
                </span>
              )}
            </div>
            <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.78rem', fontWeight: '600' }}>
              {pagination.total} student{pagination.total === 1 ? '' : 's'} in this batch
            </span>
          </div>
        )}

        {/* Statistics Cards - Default OFF */}
        {showStats && (
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
            <StatCard icon="pi pi-users" label="Total Students" value={pagination.total} sub="+ 12 students" subPositive />
            <StatCard
              icon="pi pi-check-circle"
              label="Enrolled"
              value={students.filter((s) => s.enrollment.is_enrolled).length}
              sub="+ 9%"
              subPositive
            />
            <StatCard
              icon="pi pi-id-card"
              label="Registered"
              value={students.filter((s) => s.is_registered).length}
              sub="+ 7%"
              subPositive
            />
            <StatCard
              icon="pi pi-star"
              label="Active Students"
              value={students.filter((s) => s.status === 'active').length}
              sub="+ 5%"
              subPositive
            />
          </div>
        )}

        {/* Main Table Card */}
        <div
          style={{
            background: C.white,
            borderRadius: '14px',
            border: `1px solid ${C.border}`,
            boxShadow: '0 1px 4px rgba(14,34,93,0.06)',
            overflow: 'hidden',
          }}
        >
          {/* Table Header */}
          <div
            style={{
              padding: '14px 18px',
              borderBottom: `1px solid ${C.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '0.8rem', color: C.sub }}>Showing per page</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                style={{
                  padding: '4px 8px',
                  border: `1px solid ${C.border}`,
                  borderRadius: '7px',
                  fontSize: '0.8rem',
                  color: C.navy,
                  background: C.white,
                  fontWeight: '600',
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                {[5, 10, 20, 50].map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>

            {/* Bulk Actions */}
            {selected.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 14px',
                  background: '#f8f9ff',
                  border: `1px solid ${C.border}`,
                  borderRadius: '10px',
                }}
              >
                <span style={{ fontSize: '0.8rem', fontWeight: '700', color: C.navy }}>
                  {selected.length} Selected
                </span>
                <div style={{ width: '1px', height: '16px', background: C.border }} />

                <button
                  onClick={() => {
                    setBulkManageDefaultTab('enroll');
                    setShowBulkManageModal(true);
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '5px 12px',
                    background: C.goldDim,
                    border: `1px solid ${C.goldBorder}`,
                    borderRadius: '7px',
                    color: '#b8860b',
                    fontSize: '0.78rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                  }}
                >
                  <i className="pi pi-graduation-cap" style={{ fontSize: '0.72rem' }} /> Apply Enrollment
                </button>

                <button
                  onClick={() => {
                    setBulkManageDefaultTab('deenroll');
                    setShowBulkManageModal(true);
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '5px 12px',
                    background: C.redDim,
                    border: `1px solid ${C.redBorder}`,
                    borderRadius: '7px',
                    color: C.red,
                    fontSize: '0.78rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                  }}
                >
                  <i className="pi pi-trash" style={{ fontSize: '0.72rem' }} /> De-enroll
                </button>

                <button
                  onClick={() => setSelectedStudents([])}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '26px',
                    height: '26px',
                    background: 'transparent',
                    border: `1px solid ${C.border}`,
                    borderRadius: '6px',
                    color: C.muted,
                    cursor: 'pointer',
                  }}
                >
                  <i className="pi pi-times" style={{ fontSize: '0.7rem' }} />
                </button>
              </div>
            )}
          </div>

          {/* DataTable */}
          <DataTable
            value={students}
            selection={selectedStudents}
            onSelectionChange={(e: any) => setSelectedStudents(e.value)}
            dataKey="student_id"
            loading={loading}
            emptyMessage={
              <div style={{ textAlign: 'center', padding: '40px 20px', color: C.muted }}>
                <i className="pi pi-users" style={{ fontSize: '2rem', display: 'block', marginBottom: '10px', opacity: 0.4 }} />
                <span style={{ fontSize: '0.9rem' }}>
                  {isBatchFiltered ? 'No students found in this batch' : 'No students found'}
                </span>
              </div>
            }
            className="sdt"
            style={{ fontSize: '0.83rem' }}
          >
            <Column selectionMode="multiple" headerStyle={{ width: '3rem', padding: '12px 14px' }} bodyStyle={{ padding: '12px 14px' }} />
            <Column field="name" header="Student" body={studentTemplate} headerStyle={{ minWidth: '220px' }} />
            <Column field="status" header="Status" body={statusTemplate} />
            <Column field="is_registered" header="Registration" body={registrationTemplate} headerStyle={{ minWidth: '150px' }} />
            <Column field="enrollment" header="Enrollment" body={enrollmentTemplate} headerStyle={{ minWidth: '140px' }} />
            {isBatchFiltered && (
              <Column
                field="enrollment.date_of_admission"
                header="Admission Date"
                body={(row: Student) => <span style={{ color: C.sub }}>{row.enrollment.date_of_admission || '—'}</span>}
                headerStyle={{ minWidth: '140px' }}
              />
            )}
            <Column
              field="gender"
              header="Gender"
              body={(row) => <span style={{ color: C.sub }}>{row.gender ? row.gender.charAt(0).toUpperCase() + row.gender.slice(1) : '—'}</span>}
            />
            <Column
              field="dateOfBirth"
              header="Date of Birth"
              body={(row) => <span style={{ color: C.sub }}>{formatDate(row.dateOfBirth)}</span>}
            />
            <Column header="Actions" body={actionsTemplate} headerStyle={{ width: '120px', textAlign: 'center' }} bodyStyle={{ textAlign: 'center' }} />
          </DataTable>

          {/* Pagination Footer */}
          {pagination.pages > 0 && (
            <div
              style={{
                padding: '14px 18px',
                borderTop: `1px solid ${C.border}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '10px',
              }}
            >
              <span style={{ fontSize: '0.8rem', color: C.muted }}>
                Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, pagination.total)} of {pagination.total} students
              </span>

              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  style={{
                    width: '30px',
                    height: '30px',
                    background: 'transparent',
                    border: `1px solid ${C.border}`,
                    borderRadius: '7px',
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                    opacity: page === 1 ? 0.4 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: C.navy,
                  }}
                >
                  <i className="pi pi-angle-double-left" style={{ fontSize: '0.7rem' }} />
                </button>

                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{
                    width: '30px',
                    height: '30px',
                    background: 'transparent',
                    border: `1px solid ${C.border}`,
                    borderRadius: '7px',
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                    opacity: page === 1 ? 0.4 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: C.navy,
                  }}
                >
                  <i className="pi pi-angle-left" style={{ fontSize: '0.7rem' }} />
                </button>

                {renderPages()}

                <button
                  onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  style={{
                    width: '30px',
                    height: '30px',
                    background: 'transparent',
                    border: `1px solid ${C.border}`,
                    borderRadius: '7px',
                    cursor: page === pagination.pages ? 'not-allowed' : 'pointer',
                    opacity: page === pagination.pages ? 0.4 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: C.navy,
                  }}
                >
                  <i className="pi pi-angle-right" style={{ fontSize: '0.7rem' }} />
                </button>

                <button
                  onClick={() => setPage(pagination.pages)}
                  disabled={page === pagination.pages}
                  style={{
                    width: '30px',
                    height: '30px',
                    background: 'transparent',
                    border: `1px solid ${C.border}`,
                    borderRadius: '7px',
                    cursor: page === pagination.pages ? 'not-allowed' : 'pointer',
                    opacity: page === pagination.pages ? 0.4 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: C.navy,
                  }}
                >
                  <i className="pi pi-angle-double-right" style={{ fontSize: '0.7rem' }} />
                </button>

                {/* Go to Page */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '8px' }}>
                  <span style={{ fontSize: '0.78rem', color: C.muted }}>Go to page</span>
                  <input
                    type="number"
                    min={1}
                    max={pagination.pages}
                    defaultValue={page}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const v = parseInt((e.target as HTMLInputElement).value);
                        if (v >= 1 && v <= pagination.pages) setPage(v);
                      }
                    }}
                    style={{
                      width: '46px',
                      padding: '4px 6px',
                      border: `1px solid ${C.border}`,
                      borderRadius: '7px',
                      fontSize: '0.78rem',
                      textAlign: 'center',
                      outline: 'none',
                      color: C.navy,
                    }}
                  />
                  <button
                    onClick={(e) => {
                      const inp = e.currentTarget.previousElementSibling as HTMLInputElement;
                      const v = parseInt(inp.value);
                      if (v >= 1 && v <= pagination.pages) setPage(v);
                    }}
                    style={{
                      padding: '4px 10px',
                      background: C.navy,
                      color: C.white,
                      border: 'none',
                      borderRadius: '7px',
                      fontSize: '0.78rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                    }}
                  >
                    Go
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {enrollingStudent && (
        <StudentEnrollmentModal
          visible={showEnrollmentModal}
          studentId={enrollingStudent.id}
          studentName={enrollingStudent.name}
          instituteId={INSTITUTE_ID}
          branchId={BRANCH_ID}
          existingEnrollments={enrollingStudent.existingEnrollments}
          onHide={() => {
            setShowEnrollmentModal(false);
            setEnrollingStudent(null);
          }}
          onSuccess={() => fetchStudents()}
        />
      )}

      {showBulkManageModal && selected.length > 0 && (
        <BulkManageEnrollmentsModal
          visible={showBulkManageModal}
          students={selected.map((s) => ({ student_id: s.student_id, name: s.name }))}
          instituteId={INSTITUTE_ID}
          branchId={BRANCH_ID}
          defaultTab={bulkManageDefaultTab}
          onHide={() => setShowBulkManageModal(false)}
          onSuccess={() => {
            setShowBulkManageModal(false);
            setSelectedStudents(null);
            fetchStudents();
          }}
        />
      )}
    </>
  );
};

export default Students;